import TransportDocumentLinks from '../modules/transports/components/TransportDocumentLinks';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  App,
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Space,
  Table,
  Spin,
  Tag,
  Popconfirm,
  InputNumber,
  Descriptions,
  Alert,
  Typography,
  Collapse,
  Tooltip
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  FilePdfOutlined,
  PlusOutlined,
  FileTextOutlined,
  ShopOutlined,
  InboxOutlined,
  CloseOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import WarehouseCorrectionPanel from '../modules/warehouse/WarehouseCorrectionPanel';
import { authService } from '../services/auth';
import { normalizePZDocument, PZDocumentDto } from '../dto/pz';
import { canManagePZDocuments } from '../modules/warehouse/warehouseDocumentAccessPolicy';
import {
  buildPZItemsPayload,
  hasPZItemsMissingReceivePrice,
  validatePZItemsForSave,
} from '../modules/warehouse/pzItemPayload';
import { askWarehouseSaveIntent } from '../modules/warehouse/warehouseSaveIntent';
import {
  receivePZDocument,
  type PZInventoryProcessingSummary,
} from '../modules/warehouse/pzReceiveFlow';
import { buildPZVariantAutofill } from '../modules/warehouse/pzVariantSelection';
import {
  WarehouseQuantityPatch,
  extractWarehouseQuantitySetPatch,
  formatWarehouseQuantityTotals,
  reexpressWarehouseSubquantity,
  rebaseWarehouseUnitPrice,
} from '../modules/warehouse/warehouseQuantityConversion';
import dayjs from 'dayjs';
import { formatCurrency } from '../utils/currency';
import { calculatePZDocumentTotal } from '../utils/pzTotals';
import { PZItem, PZStatus, CreatePZDto, UpdatePZDto, Supplier, ProductVariant } from '../types/pz';
import { getPZErrorMessage, isBlockingPZError } from '../modules/warehouse/pzErrorMessages';
import { useFeedback } from '../utils/feedback';
import { shouldPreventEnterFormSubmit } from '../utils/formEnterGuard';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../utils/polishNumberInput';
import { submitWithOperationalException } from '../modules/operationalExceptions/operationalExceptionFlow';
import {
  createSupplierOrderPZ,
  fetchPZTransportCandidates,
  type SupplierOrderPZGenerationOptions,
} from '../modules/orders/api/supplierOrdersApi';
import { fetchSupplierOrderTransports } from '../modules/orders/api/orderTransportApi';
import {
  mapTransportSummaryToSupplierOrderTransport,
  type SupplierOrderTransportDto,
} from '../dto/transport';
import DeliveryTransportPicker from '../modules/transports/components/DeliveryTransportPicker';
import {
  type PZTransportCandidateDto,
  type SupplierOrderPZTransportCandidateDto,
} from '../dto/pzTransportCandidates';
import { getSupplierOrderDisplayNumber, type SupplierOrderDto } from '../dto/supplierOrder';
import SupplierOrderProductsPreview from '../modules/orders/components/SupplierOrderProductsPreview';
import { buildTransportRouteLabel } from '../modules/orders/transportAddressUtils';
import { normalizeUnitCode } from '../utils/units';
import { formatQuantitySet } from '../utils/commercialQuantity';

import PZItemsSection, { type PZItemRow } from './PZItemsSection';
import { getPZOperationalStatus } from '../modules/warehouse/warehouseDocumentOperationalStatus';
import PZBatchConversionMenu from '../modules/warehouse/components/PZBatchConversionMenu';
import {
  convertPZItemsToTarget,
  type PZBatchConversionTarget,
} from '../modules/warehouse/pzBatchQuantityConversion';
import './PZDetail.css';

const { Option } = Select;
const { TextArea } = Input;

type NewPZMode = 'manual' | 'supplierOrder';

// Below the `xxl` viewport the sidebar leaves substantially less working room
// than the browser width suggests. The picker then keeps only ZD/context,
// goods and the action, so this is its safe three-column floor.
const PICKER_TABLE_MIN_WIDTH = 560;

const PZDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation(['pz', 'common', 'orders', 'transport']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [form] = Form.useForm();

  const isNewDocument = id === 'new';
  const queryParams = new URLSearchParams(location.search);
  const isEditMode = queryParams.get('mode') === 'edit' || isNewDocument;
  const initialNewPZMode = (
    queryParams.get('creationMode') === 'manual'
      ? 'manual'
      : queryParams.get('creationMode') === 'supplierOrder'
        ? 'supplierOrder'
        : 'manual'
  ) as NewPZMode;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newPZMode, setNewPZMode] = useState<NewPZMode>(() => (isNewDocument ? initialNewPZMode : 'manual'));
  // 2026-08-11 (PZ generowane z transportu): the picker's data source is now
  // transports carrying eligible ZDs, not ZDs themselves — see
  // docs/decisions/2026-08-11-pz-generated-from-transport.md.
  const [pzTransportCandidates, setPzTransportCandidates] = useState<PZTransportCandidateDto[]>([]);
  const [pzTransportCandidatesLoading, setPzTransportCandidatesLoading] = useState(false);
  const [transportSearch, setTransportSearch] = useState('');
  const [generatingSupplierOrderId, setGeneratingSupplierOrderId] = useState<string | null>(null);
  const [pzDocument, setPzDocument] = useState<PZDocumentDto | null>(null);
  const [items, setItems] = useState<PZItemRow[]>([
    { key: '0', productVariantId: '', quantityOrdered: 1, unitPrice: undefined, currency: 'PLN' }
  ]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [inventoryInfo, setInventoryInfo] = useState<PZInventoryProcessingSummary | null>(null);
  const [itemValidationError, setItemValidationError] = useState<string | null>(null);
  const [statusChanging, setStatusChanging] = useState(false);
  const statusChangeInFlightRef = useRef(false);
  // Transports linked to THIS document. Kept in component state (not in the
  // antd Form) for the same reason `items` is: the value is a list the operator
  // edits through a dedicated control, and `handleSave` assembles the payload
  // explicitly, so it never needs to round-trip through form fields.
  const [sourceTransportIds, setSourceTransportIds] = useState<string[]>([]);
  const [supplierOrderTransports, setSupplierOrderTransports] = useState<SupplierOrderTransportDto[]>([]);
  const [supplierOrderTransportsLoading, setSupplierOrderTransportsLoading] = useState(false);
  const [supplierOrderTransportsFailed, setSupplierOrderTransportsFailed] = useState(false);
  // Which supplier order (ZD) this receipt belongs to, and therefore which
  // trucks the transport picker may offer. The document itself is the
  // authoritative answer, so the section survives a cold reopen; the
  // `?supplierOrderId=` the generator appends is only a fast path that has the
  // scope one render earlier, before the document has loaded. A manual PZ has
  // neither, and correctly gets no picker.
  const sourceSupplierOrderId = pzDocument?.sourceSupplierOrderId
    || queryParams.get('supplierOrderId')
    || null;

  // Operator-facing identity of THIS document, used by every confirmation toast
  // so a success never reads as a bare "Zapisano". Empty only for an unsaved new
  // document, where the toasts that use it cannot fire yet.
  const documentNumber = pzDocument?.pzNumber || '';

  const currentUser = authService.getCurrentUser();
  const canEdit = canManagePZDocuments(currentUser);
  const canViewPZPurchasePrices = authService.canViewWarehouseDocumentPurchasePrices();
  const canEditPurchasePrices = authService.canEditPZDocumentPurchasePrices();
  const currentUserDisplayName = currentUser?.name || '';
  const isCorrection = pzDocument?.status === PZStatus.RECEIVED;
  const canEditQuantityReceived = isNewDocument
    || pzDocument?.status === PZStatus.DRAFT
    || pzDocument?.status === PZStatus.CONFIRMED
    || isCorrection;

  const fetchPZDocument = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/pz/${id}`);
      const doc = normalizePZDocument(response.data);
      setPzDocument(doc);
      // Etap 2: the transport links round-trip through component state, exactly
      // like `items` below — the fields stripped from `formValues` further down
      // stay stripped on purpose (they are not form inputs).
      setSourceTransportIds(doc.sourceTransportIds || []);
      const formValues: Record<string, unknown> = { ...doc };
      [
        'sourceTransportId',
        'sourceTransportIds',
        'sourceTransports',
        'transportCompany',
        'transportInvoiceNo',
        'transportCost',
        'transportCurrency',
        'vehicleRegistration',
        'driverName',
        'driverPhone',
        'allocateTransportCost',
      ].forEach((field) => {
        delete formValues[field];
      });

      // Set form values
      form.setFieldsValue({
        correctionReason: undefined,
        ...formValues,
        receiptDate: dayjs(doc.receiptDate),
        // Historical PZ payloads can lack issueDate even though a PZ still
        // needs to remain editable. Keep their established receipt date as a
        // one-time form fallback; every subsequent save persists it explicitly.
        issueDate: doc.issueDate ? dayjs(doc.issueDate) : dayjs(doc.receiptDate),
        expectedDeliveryDate: doc.expectedDeliveryDate ? dayjs(doc.expectedDeliveryDate) : undefined,
        supplierInvoiceDate: doc.supplierInvoiceDate ? dayjs(doc.supplierInvoiceDate) : undefined,
        supplierId: doc.supplierId,
        approvedBy: doc.approvedBy,
        receivedBy: doc.receivedBy,
      });

      // Set items
      if (doc.itemsLoaded) {
        setItems(doc.items.map((item: PZItem, index: number) => ({
          ...item,
          key: item.id || index.toString()
        })));
      }
    } catch (error: any) {
      feedback.error(getPZErrorMessage(error, t, 'pz:error.fetchDetailFailed'));
      navigate('/app/pz');
    } finally {
      setLoading(false);
    }
  }, [id, form, navigate, t, feedback]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await api.get('/suppliers?isActive=true');
      setSuppliers(response.data);
    } catch (error: any) {
      console.error('Error fetching suppliers:', error);
      feedback.error(t('pz:error.suppliersFetchFailed'));
    }
  }, [t, feedback]);

  useEffect(() => {
    fetchSuppliers();
    if (!isNewDocument) {
      fetchPZDocument();
    } else {
      // Reset any stale values left over from a previously viewed/edited PZ
      // document before starting a new one, so navigating straight from an
      // existing document's edit page into "new" mode never bleeds in the
      // old record's data.
      form.resetFields();
      setPzDocument(null);
      setItems([{ key: '0', productVariantId: '', quantityOrdered: 1, unitPrice: undefined, currency: 'PLN' }]);
      setInventoryInfo(null);
      setSourceTransportIds([]);
    }
  }, [isNewDocument, fetchPZDocument, fetchSuppliers, form]);

  useEffect(() => {
    if (!isNewDocument) {
      setNewPZMode('manual');
    }
  }, [isNewDocument]);

  useEffect(() => {
    if (!isNewDocument || newPZMode !== 'supplierOrder') {
      return;
    }

    let cancelled = false;
    const search = transportSearch.trim();
    const loadTransportCandidates = async () => {
      setPzTransportCandidatesLoading(true);
      try {
        const candidates = await fetchPZTransportCandidates({
          includeCompleted: 'true',
          search: search.length >= 3 ? search : undefined,
        });
        if (!cancelled) {
          setPzTransportCandidates(candidates);
        }
      } catch (error) {
        console.error('Error fetching PZ transport candidates:', error);
        if (!cancelled) {
          feedback.error(t('pz:generateFromSupplierOrder.loadFailed'));
        }
      } finally {
        if (!cancelled) {
          setPzTransportCandidatesLoading(false);
        }
      }
    };

    const timeout = window.setTimeout(loadTransportCandidates, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [isNewDocument, newPZMode, transportSearch, feedback, t]);

  // Etap 2: candidate trucks for a saved document. With a known ZD scope the
  // operator sees every transport on that supplier order; without it the list
  // narrows to the ones already on this PZ (still enough to review or unlink).
  const loadSupplierOrderTransports = useCallback(async () => {
    if (!sourceSupplierOrderId) {
      return;
    }

    setSupplierOrderTransportsLoading(true);
    setSupplierOrderTransportsFailed(false);
    try {
      setSupplierOrderTransports(await fetchSupplierOrderTransports(sourceSupplierOrderId));
    } catch (error) {
      setSupplierOrderTransports([]);
      setSupplierOrderTransportsFailed(true);
    } finally {
      setSupplierOrderTransportsLoading(false);
    }
  }, [sourceSupplierOrderId]);

  useEffect(() => {
    if (isNewDocument || !sourceSupplierOrderId) {
      return;
    }

    void loadSupplierOrderTransports();
  }, [isNewDocument, sourceSupplierOrderId, loadSupplierOrderTransports]);

  const linkedTransportOptions = (pzDocument?.sourceTransports || [])
    .map(mapTransportSummaryToSupplierOrderTransport);
  // Union, candidates first: a transport already on this PZ must stay visible
  // even when the candidate fetch failed or the link predates the current
  // supplier-order allocations.
  const deliveryTransportOptions = [
    ...supplierOrderTransports,
    ...linkedTransportOptions.filter(
      (linked) => !supplierOrderTransports.some((candidate) => candidate.id === linked.id),
    ),
  ];
  const canEditDeliveryTransports = isEditMode
    && !isNewDocument
    && canEdit
    && (pzDocument?.status === PZStatus.DRAFT || pzDocument?.status === PZStatus.CONFIRMED)
    && (Boolean(sourceSupplierOrderId) || linkedTransportOptions.length > 0);
  const showDeliveryTransportsSection = !isNewDocument
    && (linkedTransportOptions.length > 0 || canEditDeliveryTransports);

  const handleSupplierChange = (supplierId: string) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      form.setFieldsValue({
        supplierName: supplier.name,
        supplierAddress: supplier.address || '',
        supplierNIP: supplier.taxId || '',
      });
    }
  };

  const getSupplierOrderPZEligibleItemCount = (supplierOrder: SupplierOrderDto) => (
    (supplierOrder.items || []).filter((item) => item.pzReceipt?.eligible).length
  );

  // 2026-08-11: the picker no longer carries a line-count column ("0 / 2").
  // Counting lines answered a question no operator asked — what they weigh
  // before generating a PZ is the quantity per product, so the warehouse's
  // own per-line coverage (`pzReceipt.coveredQuantity`) now renders inside the
  // goods column, next to the ordered quantity it qualifies. Only the eligible
  // count survives, and only because it drives the button's enabled state.

  // 2026-08-11 (PZ generowane z transportu): the truck is already known from
  // the row the operator clicked — no separate "which truck?" step. Generation
  // is a single beat: ZD + the one transport that carries it.
  const handleCreatePZFromTransport = async (
    supplierOrder: SupplierOrderPZTransportCandidateDto,
    transportId: string,
  ) => {
    setGeneratingSupplierOrderId(supplierOrder.id);
    try {
      const result = await submitWithOperationalException(
        (payload) => createSupplierOrderPZ(
          supplierOrder.id,
          payload as SupplierOrderPZGenerationOptions,
        ),
        { sourceTransportIds: [transportId] },
        modal,
        t,
      );
      if (!result) {
        setGeneratingSupplierOrderId(null);
        return;
      }
      feedback.success(t('pz:generateFromSupplierOrder.created', {
        pzNumber: result.pzNumber,
        count: result.createdItemCount,
      }));
      // Carry the ZD scope forward: the PZ response strips
      // `sourceSupplierOrderId`, so this is the only way the document page can
      // offer the full truck list when she wants to correct the choice.
      navigate(`/app/pz/${result.pzDocumentId}?mode=edit&supplierOrderId=${encodeURIComponent(supplierOrder.id)}`);
    } catch (error: any) {
      feedback.error(getPZErrorMessage(error, t, 'pz:generateFromSupplierOrder.createFailed'));
      setGeneratingSupplierOrderId(null);
    }
  };

  /**
   * Closes a successful save: either that was all the operator wanted (DRAFT),
   * or the goods go on stock right away (POST). A failed receipt is not a
   * failed save — the document exists as a draft, so the operator lands ON it
   * with the sticky reason and retries from "Przyjmij towar wirtualnie".
   */
  const finishSaveWithIntent = async (
    saveIntent: 'DRAFT' | 'POST',
    savedId: string | undefined,
    savedNumber: string | undefined,
  ) => {
    if (saveIntent === 'POST' && savedId) {
      const { ok } = await receivePZDocument({ pzId: savedId, pzNumber: savedNumber, t, feedback });
      if (!ok) {
        navigate(`/app/pz/${savedId}`);
        return;
      }
    }
    navigate('/app/pz');
  };

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      if (!isNewDocument && pzDocument && !pzDocument.itemsLoaded) {
        feedback.error(t('pz:validation.itemsNotLoaded'), { sticky: true });
        setSaving(false);
        return;
      }

      const documentCurrency = values.currency || values.supplierInvoiceCurrency || 'PLN';
      const itemValidationKey = validatePZItemsForSave(items, { canEditPurchasePrices });
      if (itemValidationKey) {
        const validationMessage = t(`pz:validation.${itemValidationKey}`);
        setItemValidationError(validationMessage);
        feedback.error(validationMessage);
        setSaving(false);
        return;
      }
      setItemValidationError(null);
      const itemPayload = buildPZItemsPayload(items, {
        canEditPurchasePrices,
        documentCurrency,
      });

      // Validate items
      if (itemPayload.length === 0) {
        feedback.error(t('pz:validation.itemsRequired'));
        setSaving(false);
        return;
      }

      // Saving a PZ is a two-part decision (save + put the goods on stock?), so
      // it is asked once, here, instead of sending the operator back into the
      // saved document for a second click. A document that cannot be received
      // yet (line without a purchase price) offers the draft exit only.
      const saveIntent = isCorrection ? 'DRAFT' : await askWarehouseSaveIntent({
        documentType: 'PZ',
        modal,
        t,
        postBlockedReason: hasPZItemsMissingReceivePrice(items)
          ? t('pz:validation.priceRequiredBeforeStatus')
          : null,
      });
      if (!saveIntent) {
        setSaving(false);
        return;
      }

      const data: CreatePZDto | UpdatePZDto = {
        ...(isCorrection ? {
          correctionReason: values.correctionReason?.trim(),
          expectedUpdatedAt: pzDocument?.updatedAt,
        } : {}),
        pzNumber: values.pzNumber?.trim() || undefined,
        issueDate: values.issueDate.format('YYYY-MM-DD'),
        receiptDate: values.receiptDate.format('YYYY-MM-DD'),
        supplierId: values.supplierId,
        currency: documentCurrency,
        supplierInvoiceNumber: canEditPurchasePrices ? values.supplierInvoiceNumber : undefined,
        supplierInvoiceDate: canEditPurchasePrices ? values.supplierInvoiceDate?.format('YYYY-MM-DD') : undefined,
        supplierInvoiceAmount: canEditPurchasePrices ? values.supplierInvoiceAmount : undefined,
        supplierInvoiceCurrency: canEditPurchasePrices ? values.supplierInvoiceCurrency || documentCurrency : undefined,
        approvedBy: values.approvedBy?.trim() || null,
        receivedBy: values.receivedBy?.trim() || null,
        ...(isNewDocument ? { allocateTransportCost: false } : {}),
        // Etap 2: only sent when the operator actually had the control in front
        // of her. Sending it unconditionally would clear the links of any PZ
        // saved from a surface that never showed them.
        ...(canEditDeliveryTransports ? { sourceTransportIds } : {}),
        notes: values.notes,
        items: itemPayload,
      };

      if (isNewDocument) {
        // The number is server-assigned when the operator left the field blank,
        // so the confirmation has to come from the response, not the form.
        const created = await submitWithOperationalException(
          (confirmedPayload) => api.post('/pz', confirmedPayload),
          data as Record<string, unknown>,
          modal,
          t,
        );
        if (!created) return;
        const createdNumber = created.data?.pzNumber || data.pzNumber;
        feedback.success(createdNumber
          ? t('pz:success.created', { pzNumber: createdNumber })
          : t('pz:success.createdFallback'));
        await finishSaveWithIntent(saveIntent, created.data?.id, createdNumber);
      } else {
        const updated = await submitWithOperationalException(
          (confirmedPayload) => api.put(`/pz/${id}`, confirmedPayload),
          data as Record<string, unknown>,
          modal,
          t,
        );
        if (!updated) return;
        const savedNumber = data.pzNumber || pzDocument?.pzNumber;
        feedback.success(savedNumber
          ? t('pz:success.updated', { pzNumber: savedNumber })
          : t('pz:success.updatedFallback'));
        await finishSaveWithIntent(saveIntent, id, savedNumber);
      }
    } catch (error: any) {
      feedback.error(getPZErrorMessage(error, t, 'pz:error.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const getSelectedCurrency = () =>
    form.getFieldValue('currency') || form.getFieldValue('supplierInvoiceCurrency') || 'PLN';

  const syncDocumentCurrency = (currency: string) => {
    const nextCurrency = currency || 'PLN';
    form.setFieldsValue({
      currency: nextCurrency,
      supplierInvoiceCurrency: nextCurrency,
    });
    setItems((currentItems) => currentItems.map((item) => ({
      ...item,
      currency: nextCurrency,
    })));
  };

  const handleFormValuesChange = (changedValues: Record<string, unknown>) => {
    if (typeof changedValues.currency === 'string') {
      syncDocumentCurrency(changedValues.currency);
    } else if (typeof changedValues.supplierInvoiceCurrency === 'string') {
      syncDocumentCurrency(changedValues.supplierInvoiceCurrency);
    }
  };

  const handleStatusChange = async (newStatus: PZStatus) => {
    if (statusChangeInFlightRef.current) return;

    if ([PZStatus.CONFIRMED, PZStatus.RECEIVED].includes(newStatus)) {
      if (hasPZItemsMissingReceivePrice(items)) {
        const validationMessage = t('pz:validation.priceRequiredBeforeStatus');
        setItemValidationError(validationMessage);
        feedback.error(validationMessage);
        return;
      }
    }

    statusChangeInFlightRef.current = true;
    setStatusChanging(true);
    try {
      // The receipt path lives in modules/warehouse/pzReceiveFlow so this
      // button and the save-time choice stay one behavior.
      if (newStatus === PZStatus.RECEIVED) {
        const { ok, inventoryProcessing } = await receivePZDocument({
          pzId: id!,
          pzNumber: documentNumber,
          t,
          feedback,
        });
        if (ok && inventoryProcessing) {
          setInventoryInfo((current) => {
            const next = inventoryProcessing;
            const nextHasReceipt = Number(next.batchesCreated || 0) > 0;
            const currentHasReceipt = Number(current?.batchesCreated || 0) > 0;
            return currentHasReceipt && !nextHasReceipt ? current : next;
          });
        }
        await fetchPZDocument();
        return;
      }

      await api.put(`/pz/${id}/status`, { status: newStatus });

      if (newStatus === PZStatus.CONFIRMED) {
        feedback.success(t('pz:success.confirmed', { pzNumber: documentNumber }));
      } else if (newStatus === PZStatus.CANCELLED) {
        feedback.success(t('pz:success.cancelled', { pzNumber: documentNumber }));
      }

      await fetchPZDocument();
    } catch (error: any) {
      // RECEIVED returned above — pzReceiveFlow owns that failure sentence.
      const errorKey = newStatus === PZStatus.CONFIRMED ? 'confirmFailed' :
                       newStatus === PZStatus.CANCELLED ? 'cancelFailed' : 'saveFailed';
      // A blocked status transition is not a retry-and-hope failure: the
      // operator has to reload or fix the document, so the toast waits for her.
      feedback.error(getPZErrorMessage(error, t, `pz:error.${errorKey}`), {
        sticky: isBlockingPZError(error),
      });
    } finally {
      statusChangeInFlightRef.current = false;
      setStatusChanging(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!canViewPZPurchasePrices) {
      return;
    }

    try {
      const response = await api.get(`/pz/${id}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${pzDocument?.pzNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      feedback.success(t('pz:success.pdfDownloaded', { pzNumber: documentNumber }));
    } catch (error: any) {
      feedback.error(getPZErrorMessage(error, t, 'pz:error.pdfGenerateFailed'));
    }
  };

  const addItem = () => {
    setItems([...items, {
      key: Date.now().toString(),
      productVariantId: '',
      quantityOrdered: 1,
      unitPrice: undefined,
      currency: getSelectedCurrency()
    }]);
  };

  const removeItem = (key: string) => {
    setItems(items.filter(item => item.key !== key));
  };

  const updateItem = (key: string, field: keyof PZItemRow, value: any) => {
    setItemValidationError(null);
    const updatedItems = items.map(item => {
      if (item.key === key) {
        const updated = { ...item, [field]: value };

        // Quantity reaches state through updateItemQuantity only (the conversion
        // modal decides quantity and conversionUnit together); this path covers
        // the remaining editable fields.
        if (canEditPurchasePrices && field === 'unitPrice') {
          updated.totalPrice = (updated.purchaseQuantity ?? updated.quantityOrdered ?? 0) * (updated.unitPrice || 0);
        }

        return updated;
      }
      return item;
    });
    setItems(updatedItems);
  };

  /** Quantity, source unit, and conversion are one operator decision and must
   *  land in one state update. The catalog does not own a warehouse unit. */
  const updateItemQuantity = (key: string, patch: WarehouseQuantityPatch) => {
    setItemValidationError(null);
    setItems((current) => current.map((item) => {
      if (item.key !== key) return item;

      const updated: PZItemRow = {
        ...item,
        ...extractWarehouseQuantitySetPatch(patch),
        quantityOrdered: patch.quantity,
        unit: patch.unit,
        conversionUnit: patch.conversionUnit,
        conversionQuantity: patch.conversionQuantity,
        purchaseQuantity: patch.pricingQuantity ?? null,
        purchaseUnit: patch.pricingUnit ?? null,
      };
      if (normalizeUnitCode(item.unit) !== normalizeUnitCode(patch.unit)) {
        updated.quantityReceived = reexpressWarehouseSubquantity(
          item.quantityReceived,
          Number(item.quantityOrdered || 0),
          patch.quantity,
        );
        updated.quantityDamaged = reexpressWarehouseSubquantity(
          item.quantityDamaged ?? 0,
          Number(item.quantityOrdered || 0),
          patch.quantity,
        ) ?? 0;
      }
      if (canEditPurchasePrices) {
        const rebased = rebaseWarehouseUnitPrice({
          quantity: item.quantityOrdered,
          unit: item.unit,
          pricingQuantity: item.purchaseQuantity,
          pricingUnit: item.purchaseUnit,
          unitPrice: item.unitPrice,
          width: item.width,
          height: item.height,
          length: item.length,
          thickness: item.thickness,
        }, patch);
        updated.unitPrice = rebased.unitPrice;
        updated.totalPrice = rebased.totalValue;
      }
      return updated;
    }));
  };

  const handleBatchConversion = (target: PZBatchConversionTarget) => {
    setItemValidationError(null);
    const result = convertPZItemsToTarget(items, target, canEditPurchasePrices);
    setItems(result.items);
    if (result.convertedCount === 0) {
      feedback.warning(t('pz:batchConversion.none'));
    } else if (result.skippedCount > 0) {
      feedback.warning(t('pz:batchConversion.partial', {
        converted: result.convertedCount,
        skipped: result.skippedCount,
      }));
    } else {
      feedback.success(t('pz:batchConversion.success', { count: result.convertedCount }));
    }
  };

  // Auto-fill product info from the catalog item supplied by VariantPicker.
  // Reads the passed `variant` (not a separate fetch) so it also works for a
  // product created inline via the picker's "Add new product" flow.
  const handleVariantSelect = (key: string, variantId: string | undefined, variant?: ProductVariant) => {
    const updatedItems = items.map(item => {
      if (item.key !== key) return item;
      return {
        ...item,
        ...buildPZVariantAutofill(item, variantId, variant, canEditPurchasePrices),
      };
    });
    setItems(updatedItems);
  };

  const getStatusColor = (status: PZStatus) => {
    switch (status) {
      case PZStatus.DRAFT:
        return 'default';
      case PZStatus.CONFIRMED:
        return 'processing';
      case PZStatus.RECEIVED:
        return 'success';
      case PZStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    // Quantities from different entered units cannot collapse into one figure, so
    // each unit is totalled separately (mirrors the RAZEM row on the PZ PDF).
    const totalQuantity = formatWarehouseQuantityTotals(
      items.map((item) => ({ ...item, quantity: item.quantityOrdered })),
      i18n.language,
    );
    const totalValue = canViewPZPurchasePrices
      ? calculatePZDocumentTotal(items, form.getFieldValue('currency') || 'PLN')
      : { amount: 0, currency: form.getFieldValue('currency') || 'PLN', mixedCurrency: false };
    return { totalQuantity, totalValue };
  };


  const { totalQuantity, totalValue } = calculateTotals();

  const renderStatusActions = () => {
    if (isNewDocument || isEditMode || !pzDocument || !canEdit) {
      return null;
    }

    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Alert
          message={t(`pz:statusFlow.${getPZOperationalStatus(pzDocument.status).toLowerCase()}Info`)}
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
        />
        <Space wrap>
          {(pzDocument.status === PZStatus.DRAFT || pzDocument.status === PZStatus.CONFIRMED) && (
            <Popconfirm
              title={t('pz:confirm.receivePZ')}
              description={t('pz:confirm.receivePZDescription')}
              onConfirm={() => { void handleStatusChange(PZStatus.RECEIVED); }}
              okText={t('pz:actions.receiveVirtual')}
              cancelText={t('common:no')}
            >
              <Button type="primary" icon={<InboxOutlined />} loading={statusChanging} disabled={statusChanging}>
                {t('pz:actions.receiveVirtual')}
              </Button>
            </Popconfirm>
          )}
          {(pzDocument.status === PZStatus.DRAFT || pzDocument.status === PZStatus.CONFIRMED) && (
            <Popconfirm
              title={t('pz:confirm.cancelPZ')}
              onConfirm={() => { void handleStatusChange(PZStatus.CANCELLED); }}
              okText={t('common:yes')}
              cancelText={t('common:no')}
            >
              <Button danger icon={<CloseOutlined />} loading={statusChanging} disabled={statusChanging}>
                {t('pz:actions.cancel')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      </Space>
    );
  };

  /*
   * 2026-08-11 (PZ generowane z transportu): the picker's row is a transport,
   * not a ZD — the operator's question is "what did this truck bring in".
   * A transport carrying exactly one eligible ZD renders as a flat row with
   * every ZD-scoped cell filled in directly; a transport carrying several
   * renders those cells blank at the parent level and expands into one
   * sub-row per ZD (see `expandable` below), each with its own "Utwórz PZ".
   * The four render* helpers are shared between the flat row and the
   * sub-rows so the two never drift into different renderings of the same
   * ZD data.
   */
  const renderZdNumberCell = (
    supplierOrder: SupplierOrderPZTransportCandidateDto,
    transport?: PZTransportCandidateDto['transport'],
  ) => {
    const route = transport
      ? buildTransportRouteLabel(
        transport.loadingAddresses ?? [],
        transport.deliveryAddresses ?? [],
        transport.originCity,
        transport.destinationCity,
      )
      : null;
    const compactRoute = route?.label && route.label !== '-' ? route.label : null;
    const compactCustomer = supplierOrder.customerNames.length > 0
      ? supplierOrder.customerNames.join(', ')
      : t('pz:generateFromSupplierOrder.stockPurchase');

    return (
      <Space direction="vertical" size={2}>
        <Button
          type="link"
          style={{ padding: 0, height: 'auto' }}
          onClick={() => navigate(`/app/supplier-orders/${supplierOrder.id}`)}
        >
          {getSupplierOrderDisplayNumber(supplierOrder)}
        </Button>
        <span className="pz-transport-picker__inbound-tag">
          {t('transport:supplierOrders.inboundTag')}
        </span>
        {compactRoute || compactCustomer ? (
          <div className="pz-transport-picker__compact-context">
            {compactRoute ? (
              <span className="pz-transport-picker__compact-route">{compactRoute}</span>
            ) : null}
            {compactRoute && compactCustomer ? <span aria-hidden="true"> · </span> : null}
            {compactCustomer ? (
              <span className="pz-transport-picker__compact-customer">{compactCustomer}</span>
            ) : null}
          </div>
        ) : null}
      </Space>
    );
  };

  const renderCustomerCell = (supplierOrder: SupplierOrderPZTransportCandidateDto) => (
    supplierOrder.customerNames.length > 0 ? (
      <span>{supplierOrder.customerNames.join(', ')}</span>
    ) : (
      <Typography.Text type="secondary">
        {t('pz:generateFromSupplierOrder.stockPurchase')}
      </Typography.Text>
    )
  );

  const renderGoodsCell = (supplierOrder: SupplierOrderPZTransportCandidateDto) => (
    <SupplierOrderProductsPreview
      items={supplierOrder.items}
      showPZCoverage
      pzCoverageLabel={t('pz:generateFromSupplierOrder.coverageLabel')}
      showPZTransportScope
      pzOnTransportLabel={t('pz:generateFromSupplierOrder.onTransportLabel')}
      pzWholeOrderLabel={t('pz:generateFromSupplierOrder.wholeOrderLabel')}
      pzRemainingOnTransportLabel={t('pz:generateFromSupplierOrder.remainingOnTransportLabel')}
    />
  );

  const renderActionCell = (supplierOrder: SupplierOrderPZTransportCandidateDto, transportId: string) => {
    const documents = Array.from(new Map((supplierOrder.items || []).flatMap(item =>
      item.pzReceipt?.documents || []).map(document => [document.pzDocumentId, document])).values());
    const eligibleCount = getSupplierOrderPZEligibleItemCount(supplierOrder);
    const unavailableReason = (supplierOrder.items || [])
      .map((item) => item.pzReceipt?.blockReason)
      .find(Boolean);
    const allocationNeedsRepair = (supplierOrder.items || []).some((item) => (
      item.pzReceipt?.blockReasonCode === 'TRANSPORT_QUANTITY_UNRESOLVED'
      || item.pzReceipt?.blockReasonCode === 'TRANSPORT_ITEM_NOT_LOADED'
    ));
    return (
      <Tooltip title={eligibleCount === 0
        ? (unavailableReason || t('pz:generateFromSupplierOrder.createPZUnavailable'))
        : ''}>
        {/* antd disables pointer events on a disabled Button, so the wrapper
            is what keeps the "why can't I click this" tooltip reachable on
            fully received orders. */}
        <span className="pz-transport-picker__action-wrap">
          <TransportDocumentLinks onlyPZ documents={documents.map(document => ({
            id: document.pzDocumentId, number: document.pzNumber, status: document.status, kind: 'pz' as const,
          }))} />
          <Button
            type="primary"
            block
            disabled={eligibleCount === 0}
            loading={generatingSupplierOrderId === supplierOrder.id}
            onClick={() => { void handleCreatePZFromTransport(supplierOrder, transportId); }}
            data-testid={`pz-transport-picker-create-${supplierOrder.id}`}
          >
            {t('pz:generateFromSupplierOrder.createPZ')}
          </Button>
          {eligibleCount === 0 && allocationNeedsRepair ? (
            <Button
              type="link"
              size="small"
              block
              className="pz-transport-picker__repair-link"
              onClick={() => navigate(`/app/supplier-orders/${supplierOrder.id}`)}
            >
              {t('pz:generateFromSupplierOrder.repairAllocation')}
            </Button>
          ) : null}
        </span>
      </Tooltip>
    );
  };

  const transportPickerColumns: ColumnsType<PZTransportCandidateDto> = [
    {
      title: t('pz:generateFromSupplierOrder.zdColumn'),
      key: 'zd',
      width: 176,
      render: (_: unknown, record: PZTransportCandidateDto) => (
        record.supplierOrders.length === 1 ? (
          renderZdNumberCell(record.supplierOrders[0], record.transport)
        ) : (
          <Space direction="vertical" size={2}>
            <span className="pz-transport-picker__inbound-tag">
              {t('transport:supplierOrders.inboundTag')}
            </span>
            <span>
              {t('pz:generateFromSupplierOrder.multipleSupplierOrders', {
                count: record.supplierOrders.length,
              })}
            </span>
            <div className="pz-transport-picker__compact-context">
              <span className="pz-transport-picker__compact-route">
                {buildTransportRouteLabel(
                  record.transport.loadingAddresses ?? [],
                  record.transport.deliveryAddresses ?? [],
                  record.transport.originCity,
                  record.transport.destinationCity,
                ).label}
              </span>
            </div>
          </Space>
        )
      ),
    },
    {
      title: t('pz:generateFromSupplierOrder.routeColumn'),
      key: 'route',
      width: 136,
      responsive: ['xxl'],
      render: (_: unknown, record: PZTransportCandidateDto) => {
        const route = buildTransportRouteLabel(
          record.transport.loadingAddresses ?? [],
          record.transport.deliveryAddresses ?? [],
          record.transport.originCity,
          record.transport.destinationCity,
        );
        if (route.label === '-') {
          return <span>—</span>;
        }
        return (
          <span>
            {route.origin}
            {' → '}
            {route.destination}
          </span>
        );
      },
    },
    {
      title: t('pz:generateFromSupplierOrder.customerColumn'),
      key: 'customer',
      width: 150,
      responsive: ['xxl'],
      render: (_: unknown, record: PZTransportCandidateDto) => (
        record.supplierOrders.length === 1
          ? renderCustomerCell(record.supplierOrders[0])
          : <Typography.Text type="secondary">—</Typography.Text>
      ),
    },
    {
      title: t('pz:generateFromSupplierOrder.goods'),
      key: 'goods',
      render: (_: unknown, record: PZTransportCandidateDto) => (
        record.supplierOrders.length === 1
          ? renderGoodsCell(record.supplierOrders[0])
          : <Typography.Text type="secondary">—</Typography.Text>
      ),
    },
    {
      key: 'action',
      width: 148,
      render: (_: unknown, record: PZTransportCandidateDto) => (
        record.supplierOrders.length === 1
          ? renderActionCell(record.supplierOrders[0], record.transport.id)
          : null
      ),
    },
  ];

  const renderSupplierOrderPZPicker = () => (
    <div style={{ padding: 24, background: '#f6f7f9', minHeight: '100%' }}>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <Space align="start" size={12}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/pz')}>
            {t('common:back')}
          </Button>
          <Space direction="vertical" size={4} style={{ minWidth: 0 }}>
            <Typography.Text type="secondary">{t('pz:subtitle')}</Typography.Text>
            <Typography.Title level={2} style={{ margin: 0, letterSpacing: 0 }}>
              {t('pz:generateFromSupplierOrder.title')}
            </Typography.Title>
          </Space>
        </Space>

        <Card>
          {/*
            minWidth: 0 on every level here (Space, then the Alert itself)
            is deliberate, not decorative: this Space sits inside antd's
            nested flex chain (Card -> Space (outer, vertical) -> Sider-row
            Layout -> ...), several links of which default to
            `min-width: auto` (the browser flex default) rather than `0`.
            Left unset, a wide descendant elsewhere on the row (e.g. the
            candidates table's fixed `scroll={{ x: 1080 }}`) can starve this
            Alert of its available width without any of these boxes ever
            reporting overflow — the classic flexbox bug where a text node
            collapses toward a near-zero width and wraps one character per
            line instead of reflowing normally. See PZDetail.css for the
            matching .ant-alert rules that close the same gap on the Alert's
            own internal (icon + content) row.
          */}
          <Space direction="vertical" size={16} style={{ width: '100%', minWidth: 0 }}>
            <Alert
              className="pz-supplier-order-picker-info-alert"
              type="info"
              showIcon
              message={t('pz:generateFromSupplierOrder.infoTitle')}
              description={t('pz:generateFromSupplierOrder.infoDescription')}
            />
            <Input.Search
              allowClear
              value={transportSearch}
              onChange={(event) => setTransportSearch(event.target.value)}
              placeholder={t('pz:generateFromSupplierOrder.searchPlaceholder')}
              style={{ maxWidth: 520 }}
            />
            <Table
              className="pz-supplier-order-picker-table"
              rowKey={(record: PZTransportCandidateDto) => record.transport.id}
              columns={transportPickerColumns}
              dataSource={pzTransportCandidates}
              loading={pzTransportCandidatesLoading}
              pagination={{ pageSize: 10 }}
              expandable={{
                rowExpandable: (record) => record.supplierOrders.length > 1,
                expandedRowRender: (record) => (
                  <Table
                    className="pz-transport-picker__zd-subtable"
                    rowKey="id"
                    size="small"
                    showHeader={false}
                    pagination={false}
                    dataSource={record.supplierOrders}
                    columns={[
                      { key: 'zd', width: 176, render: (_: unknown, so: SupplierOrderPZTransportCandidateDto) => renderZdNumberCell(so, record.transport) },
                      { key: 'customer', width: 150, responsive: ['xxl'], render: (_: unknown, so: SupplierOrderPZTransportCandidateDto) => renderCustomerCell(so) },
                      { key: 'goods', render: (_: unknown, so: SupplierOrderPZTransportCandidateDto) => renderGoodsCell(so) },
                      { key: 'action', width: 148, render: (_: unknown, so: SupplierOrderPZTransportCandidateDto) => renderActionCell(so, record.transport.id) },
                    ]}
                  />
                ),
              }}
              locale={{
                emptyText: (
                  <Space direction="vertical" size={12} style={{ padding: '24px 0' }}>
                    <Typography.Text type="secondary">
                      {t('pz:generateFromSupplierOrder.empty')}
                    </Typography.Text>
                    <Button onClick={() => navigate('/app/transports/new')}>
                      {t('pz:generateFromSupplierOrder.emptyCreateTransport')}
                    </Button>
                  </Space>
                ),
              }}
              scroll={{ x: PICKER_TABLE_MIN_WIDTH }}
            />
          </Space>
        </Card>
      </Space>
    </div>
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isNewDocument && newPZMode === 'supplierOrder') {
    return renderSupplierOrderPZPicker();
  }

  return (
    <div className="pz-detail-page">
      <div className="pz-detail-page__header">
        <div className="pz-detail-heading">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/pz')}>
            {t('common:back')}
          </Button>
          <div className="pz-detail-heading__text">
            <Space wrap>
              {!isNewDocument && pzDocument && pzDocument.status && (
                <Tag color={getStatusColor(getPZOperationalStatus(pzDocument.status))} style={{ marginInlineEnd: 0 }}>
                  {t(`pz:status.${getPZOperationalStatus(pzDocument.status).toLowerCase()}`)}
                </Tag>
              )}
              <Typography.Text type="secondary">{t('pz:subtitle')}</Typography.Text>
            </Space>
            <Typography.Title level={2}>
              {isNewDocument ? t('pz:actions.create') : pzDocument?.pzNumber}
            </Typography.Title>
          </div>
        </div>
        <div className="pz-detail-actions">
          {!isNewDocument && canViewPZPurchasePrices && (
            <Button icon={<FilePdfOutlined />} onClick={handleDownloadPDF}>
              {t('pz:actions.downloadPDF')}
            </Button>
          )}
          {!isEditMode && (
            <>
              {!isNewDocument && canEdit && pzDocument?.status !== PZStatus.CANCELLED && (
                <Button onClick={() => navigate(`/app/pz/${id}?mode=edit`)}>
                  {t('common:edit')}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Inventory Info Alert after receiving */}
      {inventoryInfo && (
        <Alert
          message={t('pz:inventoryInfo.title')}
          description={
            <Descriptions column={2} size="small">
              <Descriptions.Item label={t('pz:inventoryInfo.batchCreated')}>
                {inventoryInfo.batchesCreated}
              </Descriptions.Item>
              <Descriptions.Item label={t('pz:fields.totalQuantity')}>
                {formatQuantitySet(
                  inventoryInfo as unknown as Record<string, unknown>,
                  'totalQuantity',
                  i18n.language,
                  {
                    fallbackQuantity: inventoryInfo.totalQuantity,
                    fallbackUnit: inventoryInfo.totalQuantityUnit,
                  },
                )}
              </Descriptions.Item>
              {canViewPZPurchasePrices && (
                <Descriptions.Item label={t('pz:inventoryInfo.totalCostPLN')}>
                  {formatCurrency(inventoryInfo.totalCostPLN, 'PLN')}
                </Descriptions.Item>
              )}
            </Descriptions>
          }
          type="success"
          showIcon
          icon={<InboxOutlined />}
          closable
          onClose={() => setInventoryInfo(null)}
          className="pz-detail-page__inventory-alert"
        />
      )}

      <Form
        className="pz-detail-page__form"
        form={form}
        layout="vertical"
        onFinish={handleSave}
        onValuesChange={handleFormValuesChange}
        onFinishFailed={() => feedback.warning(t('pz:fillRequired'))}
        // Live QA (agent INDIA): Enter in a single-line field (the receipt-date
        // picker, in the reported case) implicit-submitted this form. Today
        // validation happens to catch it; the guard makes that not a matter of
        // luck. Same rule as the WZ and transport forms.
        onKeyDown={(event) => {
          if (shouldPreventEnterFormSubmit(event)) {
            event.preventDefault();
          }
        }}
        scrollToFirstError
        initialValues={{
          issueDate: dayjs(),
          receiptDate: dayjs(),
          currency: 'PLN',
          supplierInvoiceCurrency: 'PLN',
          approvedBy: currentUserDisplayName,
          receivedBy: currentUserDisplayName,
        }}
      >
        {isCorrection && id && <WarehouseCorrectionPanel documentId={id} documentType="PZ" editing={isEditMode} version={pzDocument?.updatedAt} />}
        <div className="pz-detail-page__surface">
          <section className="pz-detail-section pz-detail-section--overview">
            <Collapse
              className="pz-overview-collapse"
              defaultActiveKey={['basic', 'supplier']}
              items={[
                {
                  key: 'basic',
                  label: (
                    <div className="pz-overview-collapse__label">
                      <span className="pz-overview-collapse__icon"><FileTextOutlined /></span>
                      <span>
                        <strong>{t('pz:sections.basicInfo')}</strong>
                        <small>{t('pz:sections.basicInfoHint')}</small>
                      </span>
                    </div>
                  ),
                  children: (
                    <Row gutter={[14, 0]}>
                      <Col xs={24} sm={12} lg={6}>
                        <Form.Item label={t('pz:fields.pzNumber')} name="pzNumber">
                          <Input disabled={!isEditMode} maxLength={50} placeholder={t('pz:placeholders.pzNumber')} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Form.Item
                          label={t('pz:fields.issueDate')}
                          name="issueDate"
                          rules={[{ required: true, message: t('pz:validation.issueDateRequired') }]}
                        >
                          <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" disabled={!isEditMode} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Form.Item
                          label={t('pz:fields.receiptDate')}
                          name="receiptDate"
                          rules={[{ required: true, message: t('pz:validation.receiptDateRequired') }]}
                        >
                          <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" disabled={!isEditMode} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Form.Item
                          label={t('pz:fields.currency')}
                          name="currency"
                          rules={[{ required: true, message: t('pz:validation.currencyRequired') }]}
                        >
                          <Select disabled={!isEditMode}>
                            <Option value="PLN">PLN</Option>
                            <Option value="USD">USD</Option>
                            <Option value="EUR">EUR</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  ),
                },
                {
                  key: 'supplier',
                  label: (
                    <div className="pz-overview-collapse__label">
                      <span className="pz-overview-collapse__icon"><ShopOutlined /></span>
                      <span>
                        <strong>{t('pz:sections.supplier')}</strong>
                        <small>{t('pz:sections.supplierHint')}</small>
                      </span>
                    </div>
                  ),
                  children: (
                    <>
                      <Row gutter={[14, 0]}>
                        <Col xs={24} md={12} lg={8}>
                          <Form.Item
                            label={t('pz:fields.supplier')}
                            name="supplierId"
                            rules={[{ required: true, message: t('pz:validation.supplierRequired') }]}
                          >
                            <Select
                              placeholder={t('pz:actions.selectSupplier')}
                              disabled={!isEditMode}
                              onChange={handleSupplierChange}
                              showSearch
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                (option?.children as any)?.toString().toLowerCase().includes(input.toLowerCase())
                              }
                            >
                              {suppliers.map(supplier => (
                                <Option key={supplier.id} value={supplier.id}>{supplier.name}</Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        {/* Read-only on purpose: the backend re-derives both from
                            the Supplier record on every save, so an edit here was
                            accepted by the form and then silently discarded. They
                            show what will be printed; the place to change them is
                            the supplier card. */}
                        <Col xs={24} md={12} lg={4}>
                          <Form.Item
                            label={t('pz:fields.supplierNIP')}
                            name="supplierNIP"
                            extra={isEditMode ? t('pz:fields.supplierDerivedHint') : undefined}
                          >
                            <Input disabled />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Form.Item
                            label={t('pz:fields.supplierAddress')}
                            name="supplierAddress"
                            extra={isEditMode ? t('pz:fields.supplierDerivedHint') : undefined}
                          >
                            <Input disabled />
                          </Form.Item>
                        </Col>
                      </Row>

                      {canViewPZPurchasePrices && (
                        <div className="pz-overview-collapse__invoice">
                          <Typography.Text className="pz-overview-collapse__subheading">
                            {t('pz:sections.supplierInvoice')}
                          </Typography.Text>
                          <Row gutter={[14, 0]}>
                            <Col xs={24} sm={12} lg={6}>
                              <Form.Item label={t('pz:fields.supplierInvoiceNumber')} name="supplierInvoiceNumber">
                                <Input disabled={!isEditMode} placeholder={t('pz:placeholders.supplierInvoiceNumber')} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                              <Form.Item label={t('pz:fields.supplierInvoiceDate')} name="supplierInvoiceDate">
                                <DatePicker style={{ width: '100%' }} disabled={!isEditMode} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                              <Form.Item label={t('pz:fields.supplierInvoiceAmount')} name="supplierInvoiceAmount">
                                <InputNumber
                                  style={{ width: '100%' }}
                                  disabled={!isEditMode}
                                  min={0}
                                  step={0.01}
                                  placeholder={t('pz:placeholders.supplierInvoiceAmount')}
                                  {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                              <Form.Item label={t('pz:fields.supplierInvoiceCurrency')} name="supplierInvoiceCurrency">
                                <Select disabled={!isEditMode}>
                                  <Option value="PLN">PLN</Option>
                                  <Option value="USD">USD</Option>
                                  <Option value="EUR">EUR</Option>
                                </Select>
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      )}
                    </>
                  ),
                },
              ]}
            />
          </section>

          <section className="pz-detail-section">
            <div className="pz-detail-section__heading pz-detail-section__heading--actions">
              <Typography.Title level={5}>{t('pz:sections.items')}</Typography.Title>
              <Space wrap size={10}>
                <Typography.Text type="secondary">
                  {t('pz:fields.totalQuantity')}: <strong>{totalQuantity || '-'}</strong>
                </Typography.Text>
                {canViewPZPurchasePrices && (
                  <Typography.Text type="secondary">
                    {t('pz:fields.totalValue')}: <strong>{formatCurrency(totalValue.amount, totalValue.currency)}</strong>
                  </Typography.Text>
                )}
                {isEditMode && (
                  <>
                    {canEditPurchasePrices && (
                      <PZBatchConversionMenu onConvert={handleBatchConversion} disabled={items.length === 0} />
                    )}
                    <Button type="primary" icon={<PlusOutlined />} onClick={addItem}>
                      {t('pz:actions.addItem')}
                    </Button>
                  </>
                )}
              </Space>
            </div>
            <PZItemsSection
              items={items}
              isEditMode={isEditMode}
              canViewPurchasePrices={canViewPZPurchasePrices}
              canEditPurchasePrices={canEditPurchasePrices}
              canEditQuantityReceived={canEditQuantityReceived}
              quantityTotalsLabel={totalQuantity}
              totalValue={totalValue}
              onItemChange={updateItem}
              onQuantityChange={updateItemQuantity}
              onVariantSelect={handleVariantSelect}
              onRemoveItem={removeItem}
            />
            {itemValidationError ? (
              <Alert
                type="error"
                showIcon
                message={itemValidationError}
                style={{ marginTop: 12 }}
              />
            ) : null}
          </section>

          {/* Etap 2 (transport przywozowy): which truck this receipt came off.
              Read-only once the document is RECEIVED/CANCELLED — the stock is
              already booked by then and the trace must not move under it. */}
          {showDeliveryTransportsSection ? (
            <section className="pz-detail-section" data-testid="pz-delivery-transports-section">
              <div className="pz-detail-section__heading pz-detail-section__heading--stacked">
                <Typography.Title level={5}>{t('pz:deliveryTransports.title')}</Typography.Title>
                <Typography.Text type="secondary" className="pz-detail-section__hint">
                  {canEditDeliveryTransports
                    ? t('pz:deliveryTransports.editHint')
                    : t('pz:deliveryTransports.viewHint')}
                </Typography.Text>
              </div>
              <DeliveryTransportPicker
                transports={deliveryTransportOptions}
                value={sourceTransportIds}
                onChange={canEditDeliveryTransports ? setSourceTransportIds : undefined}
                readOnly={!canEditDeliveryTransports}
                hideNoTransportOption
                loading={supplierOrderTransportsLoading}
                loadFailed={supplierOrderTransportsFailed && deliveryTransportOptions.length === 0}
                onRetry={() => { void loadSupplierOrderTransports(); }}
                data-testid="pz-delivery-transport-picker"
              />
            </section>
          ) : null}

          <section className="pz-detail-section">
            <div className="pz-detail-section__heading">
              <Typography.Title level={5}>{t('pz:sections.signature')}</Typography.Title>
            </div>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t('pz:fields.approvedBy')}
                  name="approvedBy"
                  rules={[{ max: 100, message: t('pz:validation.personNameMax') }]}
                >
                  <Input disabled={!isEditMode} placeholder={t('pz:placeholders.approvedBy')} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t('pz:fields.receivedBy')}
                  name="receivedBy"
                  rules={[{ max: 100, message: t('pz:validation.personNameMax') }]}
                >
                  <Input disabled={!isEditMode} placeholder={t('pz:placeholders.receivedBy')} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="notes" label={t('pz:fields.notes')} style={{ marginBottom: 0 }}>
              <TextArea rows={3} disabled={!isEditMode} placeholder={t('pz:placeholders.notes')} />
            </Form.Item>
          </section>

        </div>

        {isEditMode && (
          <div className="pz-detail-page__sticky-actions">
            <Button onClick={() => navigate('/app/pz')}>{t('common:cancel')}</Button>
            <Button type="primary" loading={saving} icon={<SaveOutlined />} htmlType="submit">
              {t('common:save')}
            </Button>
          </div>
        )}
      </Form>

      {!isNewDocument && !isEditMode && pzDocument && canEdit && (
        <div className="pz-detail-page__secondary-surface">
          <Collapse
            ghost
            items={[{
              key: 'warehouse',
              label: t('pz:sections.statusActions'),
              children: renderStatusActions(),
            }]}
          />
        </div>
      )}

    </div>
  );
};

export default PZDetail;
